(() => {
  if (window.__spProductImageZoomLoaded) return;
  window.__spProductImageZoomLoaded = true;

  const TRIGGER_SELECTOR = [
    '.product-information__thumbnail-gallery-panel .product-media__image',
    '.product-information__direct-media-fallback-image',
  ].join(', ');
  const ZOOMED_CLASS = 'is-zoomed';
  const OPEN_CLASS = 'sp-product-zoom-open';

  let dialog;
  let viewport;
  let zoomImage;
  let closeButton;
  let previousButton;
  let nextButton;
  let counter;
  let items = [];
  let currentIndex = 0;
  let lastTrigger = null;
  let isZoomed = false;
  let touchStartX = null;
  let touchStartY = null;

  const getImageSource = (element) =>
    element.getAttribute('data-max-resolution') ||
    element.getAttribute('data_max_resolution') ||
    element.getAttribute('src') ||
    element.currentSrc;

  const decorateTriggers = (root = document) => {
    root.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.tabIndex = 0;
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-label', `View larger image${trigger.alt ? `: ${trigger.alt}` : ''}`);
    });
  };

  const setZoom = (zoomed) => {
    if (!dialog || !viewport) return;

    isZoomed = zoomed;
    dialog.classList.toggle(ZOOMED_CLASS, isZoomed);

    requestAnimationFrame(() => {
      if (isZoomed) {
        viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
        viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
      } else {
        viewport.scrollTo({ top: 0, left: 0 });
      }
    });
  };

  const renderCurrentImage = () => {
    if (!zoomImage || items.length === 0) return;

    currentIndex = (currentIndex + items.length) % items.length;
    const sourceImage = items[currentIndex];

    setZoom(false);
    zoomImage.src = getImageSource(sourceImage);
    zoomImage.alt = sourceImage.alt || 'Product image';

    const hasMultipleImages = items.length > 1;
    previousButton.hidden = !hasMultipleImages;
    nextButton.hidden = !hasMultipleImages;
    counter.hidden = !hasMultipleImages;
    counter.textContent = `${currentIndex + 1} / ${items.length}`;
  };

  const showPrevious = () => {
    if (items.length < 2) return;
    currentIndex -= 1;
    renderCurrentImage();
  };

  const showNext = () => {
    if (items.length < 2) return;
    currentIndex += 1;
    renderCurrentImage();
  };

  const createDialog = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'sp-product-zoom';
    dialog.setAttribute('aria-label', 'Product image viewer');
    dialog.innerHTML = `
      <div class="sp-product-zoom__shell">
        <button type="button" class="sp-product-zoom__control sp-product-zoom__close" aria-label="Close image viewer">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <button type="button" class="sp-product-zoom__control sp-product-zoom__previous" aria-label="Previous product image">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <div class="sp-product-zoom__viewport">
          <img class="sp-product-zoom__image" alt="" draggable="false">
        </div>
        <button type="button" class="sp-product-zoom__control sp-product-zoom__next" aria-label="Next product image">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7" /></svg>
        </button>
        <p class="sp-product-zoom__counter" aria-live="polite"></p>
      </div>
    `;

    document.body.appendChild(dialog);

    viewport = dialog.querySelector('.sp-product-zoom__viewport');
    zoomImage = dialog.querySelector('.sp-product-zoom__image');
    closeButton = dialog.querySelector('.sp-product-zoom__close');
    previousButton = dialog.querySelector('.sp-product-zoom__previous');
    nextButton = dialog.querySelector('.sp-product-zoom__next');
    counter = dialog.querySelector('.sp-product-zoom__counter');

    closeButton.addEventListener('click', () => dialog.close());
    previousButton.addEventListener('click', showPrevious);
    nextButton.addEventListener('click', showNext);
    zoomImage.addEventListener('click', () => setZoom(!isZoomed));

    viewport.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1 || isZoomed) {
          touchStartX = null;
          touchStartY = null;
          return;
        }

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      (event) => {
        if (touchStartX === null || touchStartY === null || isZoomed || event.changedTouches.length !== 1) return;

        const deltaX = event.changedTouches[0].clientX - touchStartX;
        const deltaY = event.changedTouches[0].clientY - touchStartY;
        touchStartX = null;
        touchStartY = null;

        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        if (deltaX > 0) showPrevious();
        else showNext();
      },
      { passive: true }
    );

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove(OPEN_CLASS);
      setZoom(false);
      zoomImage.removeAttribute('src');
      if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
      lastTrigger = null;
    });
  };

  const openViewer = (trigger) => {
    const scope = trigger.closest('.product-information') || document;
    items = Array.from(scope.querySelectorAll(TRIGGER_SELECTOR)).filter((image) => getImageSource(image));
    currentIndex = Math.max(0, items.indexOf(trigger));
    lastTrigger = trigger;

    if (!dialog) createDialog();
    renderCurrentImage();
    document.documentElement.classList.add(OPEN_CLASS);
    dialog.showModal();
    closeButton.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger || dialog?.contains(trigger)) return;
    openViewer(trigger);
  });

  document.addEventListener('keydown', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;

    if (trigger && !dialog?.open && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openViewer(trigger);
      return;
    }

    if (!dialog?.open) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showPrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      showNext();
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      setZoom(true);
    } else if (event.key === '-') {
      event.preventDefault();
      setZoom(false);
    }
  });

  document.addEventListener('shopify:section:load', (event) => decorateTriggers(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => decorateTriggers(document), { once: true });
  } else {
    decorateTriggers(document);
  }
})();
